import { TarifaDetalle } from '../../domain/tarifa-detalle.domain';
import { TipoProducto } from '../../domain/tipo-producto.domain';

export type TarifaDetalleIssueType = 'ERROR' | 'WARN';
export type TarifaDetalleIssueCode = 'REQUIRED' | 'DUPLICATE' | 'OVERLAP';

export interface TarifaDetalleAnalyzerIssue {
  type: TarifaDetalleIssueType;
  code: TarifaDetalleIssueCode;
  rowIndex: number;
  detalleId?: number | null;
  fields: string[];
  message: string;
  key?: string;
  relatedRowIndexes?: number[];
}

export interface TarifaDetalleAnalyzerRow {
  index: number;
  detalle: TarifaDetalle;
  tipoProducto?: TipoProducto | null;
}

export interface TarifaDetalleAnalyzerResult {
  issues: TarifaDetalleAnalyzerIssue[];
  keyByRow: Record<number, string>;
  blocking: boolean;
  warningCount: number;
  errorCount: number;
}

const ANY = '*';

export function analyzeTarifaDetalles(rows: TarifaDetalleAnalyzerRow[]): TarifaDetalleAnalyzerResult {
  const issues: TarifaDetalleAnalyzerIssue[] = [];
  const keyByRow: Record<number, string> = {};

  const enriched = rows.map((row) => {
    const tipo = row.tipoProducto ?? asTipoProducto(row.detalle.tipoProducto);
    const tipoCodigo = tipo?.codigo ?? null;
    const matchingKey = buildMatchingKey(row.detalle, tipo);
    const baseKey = buildMatchingKey(row.detalle, tipo, true);
    const timeRange = extractTimeRange(row.detalle);
    keyByRow[row.index] = matchingKey;

    const missingFields = getMissingRequiredFields(row.detalle, tipo);
    if (missingFields.length > 0) {
      issues.push({
        type: 'ERROR',
        code: 'REQUIRED',
        rowIndex: row.index,
        detalleId: row.detalle.id ?? null,
        fields: missingFields,
        message: `Faltan campos requeridos: ${missingFields.join(', ')}.`,
        key: matchingKey
      });
    }

    return {
      row,
      tipo,
      tipoCodigo,
      baseKey,
      matchingKey,
      timeRange
    };
  });

  const grouped = new Map<string, typeof enriched>();
  enriched.forEach((entry) => {
    const key = entry.baseKey || ANY;
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)!.push(entry);
  });

  grouped.forEach((entries) => {
    if (entries.length <= 1) return;

    for (let i = 0; i < entries.length; i++) {
      for (let j = i + 1; j < entries.length; j++) {
        const a = entries[i];
        const b = entries[j];
        const sameRange = rangesEqual(a.timeRange, b.timeRange);
        const overlaps = rangesOverlap(a.timeRange, b.timeRange);

        if (sameRange) {
          issues.push({
            type: 'ERROR',
            code: 'DUPLICATE',
            rowIndex: a.row.index,
            detalleId: a.row.detalle.id ?? null,
            fields: ['duplicate'],
            message: `Duplicado exacto con fila #${b.row.index + 1}.`,
            key: a.matchingKey,
            relatedRowIndexes: [b.row.index]
          });
          issues.push({
            type: 'ERROR',
            code: 'DUPLICATE',
            rowIndex: b.row.index,
            detalleId: b.row.detalle.id ?? null,
            fields: ['duplicate'],
            message: `Duplicado exacto con fila #${a.row.index + 1}.`,
            key: b.matchingKey,
            relatedRowIndexes: [a.row.index]
          });
        } else if (overlaps) {
          issues.push({
            type: 'WARN',
            code: 'OVERLAP',
            rowIndex: a.row.index,
            detalleId: a.row.detalle.id ?? null,
            fields: ['overlap'],
            message: `Solapa con fila #${b.row.index + 1}.`,
            key: a.matchingKey,
            relatedRowIndexes: [b.row.index]
          });
          issues.push({
            type: 'WARN',
            code: 'OVERLAP',
            rowIndex: b.row.index,
            detalleId: b.row.detalle.id ?? null,
            fields: ['overlap'],
            message: `Solapa con fila #${a.row.index + 1}.`,
            key: b.matchingKey,
            relatedRowIndexes: [a.row.index]
          });
        }
      }
    }
  });

  const errorCount = issues.filter((issue) => issue.type === 'ERROR').length;
  const warningCount = issues.filter((issue) => issue.type === 'WARN').length;

  return {
    issues,
    keyByRow,
    blocking: errorCount > 0,
    warningCount,
    errorCount
  };
}

export function buildMatchingKey(detalle: TarifaDetalle, tipo?: TipoProducto | null, baseOnly: boolean = false): string {
  const resolvedTipo = tipo ?? asTipoProducto(detalle.tipoProducto);
  const codigo = resolvedTipo?.codigo ?? 'N/A';
  const unidad = normalizeValue(detalle.unidadTarifacion);

  const parts: string[] = [codigo];

  if (codigo === 'AIRE_SPOT') {
    parts.push(normalizeValue(detalle.diaTipo));
    if (!baseOnly) {
      parts.push(formatTimeRange(detalle));
    }
    parts.push(`PROG:${normalizeValue(extractId(detalle.programa))}`);
    parts.push(`MAT:${normalizeValue(extractId(detalle.tipoMaterial))}`);
    parts.push(`UN:${unidad}`);
    return parts.join('|');
  }

  if (codigo === 'WEB_BANNER' || codigo === 'REDES_PIEZA') {
    parts.push(`FORM:${normalizeValue(extractId(detalle.formatoDigital))}`);
    parts.push(`UBI:${normalizeValue(extractId(detalle.ubicacionDigital))}`);
    if (codigo === 'REDES_PIEZA') {
      parts.push(`PLAT:${normalizeValue(detalle.plataformaRedes)}`);
    }
    parts.push(`UN:${unidad}`);
    return parts.join('|');
  }

  if (codigo === 'PNT') {
    parts.push(`UN:${unidad}`);
    return parts.join('|');
  }

  parts.push(`UN:${unidad}`);
  return parts.join('|');
}

function getMissingRequiredFields(detalle: TarifaDetalle, tipo?: TipoProducto | null): string[] {
  const missing: string[] = [];
  const codigo = tipo?.codigo ?? asTipoProducto(detalle.tipoProducto)?.codigo ?? '';
  const canal = tipo?.canal ?? null;

  if (!detalle.unidadTarifacion) {
    missing.push('unidadTarifacion');
  }
  if (detalle.precioUnitario === null || detalle.precioUnitario === undefined || detalle.precioUnitario === '') {
    missing.push('precioUnitario');
  }

  if (codigo === 'AIRE_SPOT' || tipo?.requiereDiaTipo) {
    if (!detalle.diaTipo) {
      missing.push('diaTipo');
    }
    if (tipo?.requiereFranjaHoraria || codigo === 'AIRE_SPOT') {
      if (!detalle.horaDesde) {
        missing.push('horaDesde');
      }
      if (!detalle.horaHasta) {
        missing.push('horaHasta');
      }
    }
  }

  const isDigital = canal === 'WEB' || canal === 'REDES' || codigo === 'WEB_BANNER' || codigo === 'REDES_PIEZA';
  if (isDigital && (tipo?.requiereFormatoDigital || codigo === 'WEB_BANNER' || codigo === 'REDES_PIEZA')) {
    if (!detalle.formatoDigital) {
      missing.push('formatoDigital');
    }
  }
  if (isDigital && (tipo?.requiereUbicacionDigital || codigo === 'WEB_BANNER')) {
    if (!detalle.ubicacionDigital) {
      missing.push('ubicacionDigital');
    }
  }

  if (codigo === 'REDES_PIEZA' && !detalle.plataformaRedes) {
    missing.push('plataformaRedes');
  }

  if (codigo === 'PNT') {
    if (!detalle.unidadTarifacion) {
      missing.push('unidadTarifacion');
    }
  }

  return Array.from(new Set(missing));
}

function extractTimeRange(detalle: TarifaDetalle): { start: number | null; end: number | null } {
  return {
    start: toMinutes(detalle.horaDesde),
    end: toMinutes(detalle.horaHasta)
  };
}

function formatTimeRange(detalle: TarifaDetalle): string {
  const start = toMinutes(detalle.horaDesde);
  const end = toMinutes(detalle.horaHasta);
  if (start === null && end === null) {
    return 'ALL';
  }
  return `${formatMinutes(start)}-${formatMinutes(end)}`;
}

function formatMinutes(value: number | null): string {
  if (value === null) return '??:??';
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

function rangesEqual(
  a: { start: number | null; end: number | null },
  b: { start: number | null; end: number | null }
): boolean {
  return a.start === b.start && a.end === b.end;
}

function rangesOverlap(
  a: { start: number | null; end: number | null },
  b: { start: number | null; end: number | null }
): boolean {
  const aSegments = normalizeRange(a.start, a.end);
  const bSegments = normalizeRange(b.start, b.end);
  for (const segA of aSegments) {
    for (const segB of bSegments) {
      const overlap = Math.max(segA.start, segB.start) < Math.min(segA.end, segB.end);
      if (overlap) {
        return true;
      }
    }
  }
  return false;
}

function normalizeRange(start: number | null, end: number | null): Array<{ start: number; end: number }> {
  const startValue = start ?? 0;
  const endValue = end ?? 24 * 60;
  if (startValue === endValue) {
    return [];
  }
  if (endValue < startValue) {
    return [
      { start: startValue, end: 24 * 60 },
      { start: 0, end: endValue }
    ];
  }
  return [{ start: startValue, end: endValue }];
}

function toMinutes(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const timeRegex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
    const match = trimmed.match(timeRegex);
    if (match) {
      const hours = parseInt(match[1], 10);
      const minutes = parseInt(match[2], 10);
      const seconds = match[3] ? parseInt(match[3], 10) : 0;
      if (hours === 24 && minutes === 0 && seconds === 0) {
        return 24 * 60;
      }
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        if (hours === 23 && minutes === 59 && seconds === 59) {
          return 24 * 60;
        }
        return hours * 60 + minutes;
      }
    }
  }

  if (value instanceof Date) {
    if (isNaN(value.getTime())) {
      return null;
    }
    return value.getHours() * 60 + value.getMinutes();
  }

  return null;
}

function normalizeValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return ANY;
  }
  return String(value).trim().toUpperCase();
}

function extractId(value: any): string | null {
  if (!value) return null;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    const match = value.match(/\/(\d+)$/);
    if (match) return match[1];
    if (/^\d+$/.test(value)) return value;
    return value;
  }
  if (typeof value === 'object') {
    if (value.id !== undefined && value.id !== null) return String(value.id);
  }
  return null;
}

function asTipoProducto(value: any): TipoProducto | null {
  if (!value || typeof value === 'string' || typeof value === 'number') {
    return null;
  }
  return value as TipoProducto;
}
